<script setup lang="ts">
import { computed, onBeforeMount, provide, ref } from 'vue';
import { Router } from 'vue-router';

import {
  FilterMatchMode,
  TSButton,
  TSButtonFilter,
  TSButtonSearch,
  TSButtonSearchByScan,
  TSDialogConfirm,
  TSIcon,
  TSToast,
  useLoadingStore,
  useToast,
} from 'tsv2-library';
import { ScanPayload } from 'tsv2-library/dist/src/components/v2/ButtonSearchByScan/ButtonSearchByScan.vue';
import { Asset } from 'tsv2-library/dist/src/types/assets.type';

import LinkedAssetDialog from '@/components/common/Dialog/LinkedAssetDialog.vue';
import BorrowReturnBulkAction from './BorrowReturnBulkAction.vue';
import BorrowReturnFilter from './BorrowReturnFilter.vue';
import BorrowReturnTable from './BorrowReturnTable.vue';

import AssetServices from '@/services/asset.service';
import BorrowingServices from '@/services/borrowing.service';

import { BorrowingBorrowedReturnAsset } from '@/types/borrowing.type';
import { BorrowingRequestFilters } from '@/types/filter.type';

onBeforeMount(async () => {
  try {
    // Get the request ids from session storage.
    requestIds.value = JSON.parse(
      sessionStorage.getItem('assetBorrowReturn') ?? '',
    );

    if (requestIds.value) {
      await getAllBorrowingRequestData();

      // Update the table.
      window.dispatchEvent(
        new CustomEvent('updateTable', {
          detail: {
            tableName: 'borrow-return-table',
          },
        }),
      );
    } else {
      throw new Error();
    }
  } catch (error) {
    console.error(error);

    if (props.router) props.router.back();
  }
});

const props = defineProps<{
  router?: Router;
}>();

const toast = useToast();
const { setLoading } = useLoadingStore();

const requestIds = ref<string[]>(); // Borrowing request _id

const searchModel = ref<string>();

const formData = ref<Map<string, BorrowingBorrowedReturnAsset>>(new Map());

const formDataArray = computed<BorrowingBorrowedReturnAsset[]>(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return Array.from(formData.value, ([_, value]) => value);
});
const selectedData = ref<BorrowingBorrowedReturnAsset[]>([]);
const verifyScan = ref();

const isDataVerifiedAll = computed<boolean>(() => {
  return (
    formDataArray.value
      .flatMap((datum) => (datum.children ? [datum, ...datum.children] : datum))
      .filter((datum) => !datum.isVerified).length === 0
  );
});

const flatVerifiedData = computed(() => {
  return formDataArray.value.filter((datum) => {
    if (datum.children && datum.children.length > 0) {
      return datum.isVerified && datum.children.every((d) => d.isVerified);
    }
    return datum.isVerified;
  });
});

const showFilter = ref<boolean>(false);

const showCancelReturn = ref<boolean>(false);
const showSubmitReturn = ref<boolean>(false);
const showLinkedAssetConfirm = ref<boolean>(false);

const filterParams = ref<{
  [key: string]: {
    value?: null | string[] | number[];
    matchMode: number;
  };
}>({
  'name.name': { value: null, matchMode: FilterMatchMode.IN },
  'brand.name': { value: null, matchMode: FilterMatchMode.IN },
  'model.name': { value: null, matchMode: FilterMatchMode.IN },
  'tagType': { value: null, matchMode: FilterMatchMode.IN },
});

/**
 * Get borrow transaction request all data.
 */
const getAllBorrowingRequestData = async (): Promise<void> => {
  try {
    setLoading(true);

    const { data } = await BorrowingServices.getBorrowingTransactionRequestList(
      {
        requestIds: JSON.stringify(requestIds.value),
      },
    );

    data.data.data.forEach((datum) => {
      formData.value.set(datum._id, {
        ...datum,
        isVerified: datum.tagType === 'Non TAG',
        children: datum.children
          ? datum.children.map((d) => ({
              ...d,
              isVerified: d.tagType === 'Non TAG',
            }))
          : [],
      });
    });
  } catch (error) {
    toast.add({
      error,
      message: 'Error, failed to fetch transaction request.',
      severity: 'error',
    });
  } finally {
    setLoading(false);
  }
};

const assignFilterParams = (filters: BorrowingRequestFilters): void => {
  filterParams.value['name.name'].value = filters.name;
  filterParams.value['brand.name'].value = filters.brand;
  filterParams.value['model.name'].value = filters.model;
  filterParams.value['tagType'].value = filters.tagType;
};

const handleCancel = (): void => {
  sessionStorage.removeItem('assetBorrowBreadcrumb');
  sessionStorage.removeItem('assetBorrowReturn');

  if (props.router) props.router.back();
};

/**
 * Handle the scan button for asset verification.
 */
const handleScan = async (payload: ScanPayload): Promise<void> => {
  try {
    setLoading(true);

    const { data } = await AssetServices.getAsset({
      tag: payload.code,
    });

    const reqIds = data.data.data.map((datum: Asset) => datum._id);

    formDataArray.value.forEach((datum) => {
      if (reqIds.includes(datum.asset)) {
        datum.isVerified = true;
      }
      datum.children?.forEach((d) => {
        if (reqIds.includes(d.asset)) {
          d.isVerified = true;
        }
      });
    });

    // Stop scan when all data is verified.
    if (isDataVerifiedAll.value && verifyScan.value) {
      verifyScan.value.stopScan();
    }
  } catch (error) {
    toast.removeGroup('return-bulk-scan');
    toast.add({
      error,
      message: 'The asset is not in the return list.',
      severity: 'error',
    });
  } finally {
    setLoading(false);
  }
};

/**
 * Handle the submit button
 */
const handleSubmit = async (): Promise<void> => {
  try {
    setLoading(true);

    await BorrowingServices.putBorrowingReturn(
      flatVerifiedData.value.map((datum) => datum._id),
    );

    toast.add({
      message: 'Success, returned asset has been submitted.',
      severity: 'success',
    });

    sessionStorage.removeItem('assetBorrowBreadcrumb');
    sessionStorage.removeItem('assetBorrowReturn');

    if (props.router) props.router.back();
  } catch (error) {
    toast.add({
      error,
      message: 'Error, failed to return asset.',
      severity: 'error',
    });
  } finally {
    setLoading(false);
  }
};

const isLinkedAsset = computed(() =>
  flatVerifiedData.value.some(
    (datum) => datum.children && datum.children.length > 0,
  ),
);

const handleReportTAG = async (): Promise<void> => {
  await getAllBorrowingRequestData();

  dispatchEvent(
    new CustomEvent('updateTable', {
      detail: {
        tableName: 'borrow-return-table',
      },
    }),
  );
};

provide('formData', formData);
provide('isLinkedAsset', isLinkedAsset);
</script>
<template>
  <TSToast
    base-z-index="9997"
    class="!absolute !bottom-[80px] !h-max"
    group="handover-return-scan"
  />
  <LinkedAssetDialog
    v-model:visible="showLinkedAssetConfirm"
    :data="flatVerifiedData"
    @confirm="handleSubmit"
    mode="Confirmation"
  />
  <TSDialogConfirm
    v-model:visible="showCancelReturn"
    @confirm="handleCancel"
    close-after-confirm
    confirm-label="Continue"
    data-ts-section="cancel-return-dialog"
    header="Cancel Borrowing Return"
    severity="danger"
  >
    <template #body>
      <span>This action will cancel the return.</span>
      <span>Are you sure want to continue?</span>
    </template>
  </TSDialogConfirm>
  <TSDialogConfirm
    v-model:visible="showSubmitReturn"
    @confirm="isLinkedAsset ? (showLinkedAssetConfirm = true) : handleSubmit()"
    close-after-confirm
    confirm-label="Continue"
    data-ts-section="submit-return-dialog"
    header="Return Asset"
    severity="success"
  >
    <template #body>
      <span>
        {{
          formDataArray
            .flatMap((datum) =>
              datum.children ? [datum, ...datum.children] : datum,
            )
            .filter((datum) => !datum.isVerified).length
        }}
        unverified asset(s) will be removed from the return process.
      </span>
      <span>Are you sure want to continue?</span>
    </template>
  </TSDialogConfirm>
  <div class="flex flex-col gap-3" data-ts-section="borrow-return-header">
    <span
      class="text-[16px] text-general-800 font-bold leading-[normal] tracking-[0.32px]"
      data-ts-section="borrow-return-title"
    >
      Asset Verification
    </span>
    <div
      class="flex flex-row justify-between items-center"
      data-ts-section="borrow-return-header-group"
    >
      <BorrowReturnBulkAction v-model:selected-data="selectedData" />
      <div
        class="flex flex-row gap-2 items-center"
        data-ts-section="borrow-return-header-action-group"
      >
        <div
          class="flex flex-row gap-1 items-center"
          data-ts-section="borrow-return-header-action"
        >
          <TSIcon
            icon="info"
            info="Non TAG asset will automatically verified without scanning"
            severity="primary"
          />
          <TSButtonSearchByScan
            ref="verifyScan"
            @scan="handleScan"
            bulk
            label="Verify Asset"
          />
          <TSButtonSearch
            @search="searchModel = $event"
            data-ts-section="borrow-return-search-button"
          />
          <TSButtonFilter
            v-model:show-filter="showFilter"
            data-ts-section="borrow-return-filter-button"
          />
        </div>
      </div>
    </div>
  </div>
  <BorrowReturnFilter
    v-show="showFilter"
    :assets="formDataArray"
    @apply-filter="assignFilterParams"
  />
  <BorrowReturnTable
    v-model:form-data="formData"
    v-model:selected-data="selectedData"
    :data="formDataArray"
    :filters="filterParams"
    :search="searchModel"
    @reported="handleReportTAG"
  />
  <div
    class="flex flex-col gap-1 items-end"
    data-ts-section="borrow-return-footer"
  >
    <div class="flex gap-1 items-center">
      <TSButton
        @click="showCancelReturn = true"
        data-ts-section="cancel-button"
        label="Cancel"
        severity="dark"
        text
      />
      <TSButton
        @click="
          formDataArray
            .flatMap((datum) =>
              datum.children ? [datum, ...datum.children] : datum,
            )
            .filter((datum) => !datum.isVerified).length > 0
            ? (showSubmitReturn = true)
            : isLinkedAsset
              ? (showLinkedAssetConfirm = true)
              : handleSubmit()
        "
        data-ts-section="submit-button"
        label="Submit"
        severity="success"
      />
    </div>
  </div>
</template>
